/**
 * Route ZIP Code Field
 *
 * Renders a required ZIP Code input when a selected route is assigned by
 * territory, which is MSL and MIL. Medical Information is a central team and
 * needs no zip.
 *
 * The code is stamped onto the conversation for whoever picks it up. Nothing
 * resolves it to a person, so a zip that maps to no one in a territory fails
 * silently rather than at submit.
 */

import { AlertCircleIcon } from 'lucide-react-native';
import { Controller, useWatch, type Control, type FieldErrors } from 'react-hook-form';

import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
  FormControlLabel,
  FormControlLabelText,
} from '@/components/ui/form-control';
import { Input, InputField } from '@/components/ui/input';
import { TERRITORY_ROUTES } from '@/constants/medicalOnDemand';
import type { MODFormData } from '@/hooks/useMODInquiryForm';

type RouteZipCodeFieldProps = {
  control: Control<MODFormData>;
  errors: FieldErrors<MODFormData>;
  isDisabled?: boolean;
};

export function RouteZipCodeField({ control, errors, isDisabled = false }: RouteZipCodeFieldProps) {
  const routes = useWatch({ control, name: 'routes' }) ?? [];

  if (!routes.some((route) => TERRITORY_ROUTES.includes(route))) {
    return null;
  }

  return (
    <Controller
      name="postalCode"
      control={control}
      render={({ field: { onChange, onBlur, value }, fieldState, formState }) => {
        const showError =
          !!errors.postalCode?.message && (fieldState.isTouched || formState.isSubmitted);

        return (
          <FormControl isInvalid={showError} isDisabled={isDisabled}>
            <FormControlLabel className="mb-2">
              <FormControlLabelText
                size="sm"
                className="font-semibold leading-normal text-typography-950">
                Confirm your ZIP Code*
              </FormControlLabelText>
            </FormControlLabel>

            <Input>
              <InputField
                testID="mod-postal-code-field"
                placeholder="e.g. 43212"
                value={value ?? ''}
                onChangeText={onChange}
                onBlur={onBlur}
                inputMode="numeric"
                keyboardType="numeric"
                autoComplete="postal-code"
                maxLength={10}
              />
            </Input>

            <FormControlError className="mt-2">
              <FormControlErrorIcon as={AlertCircleIcon} />
              <FormControlErrorText size="xs">{errors.postalCode?.message}</FormControlErrorText>
            </FormControlError>
          </FormControl>
        );
      }}
    />
  );
}
